import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { getContacts, setAuthToken, getQualifications, getVilles, getUploadsUrl , restoreContact, deleteContactPermanent } from '../services/api';
import { toast } from 'react-toastify';
import MultiSelect from '../components/MultiSelect';
import { Gate, usePerm } from '../lib/perms';
import './ContactsList.css';

const BORNE_TYPE_COLORS = {
  // Classik passe d'un bleu pale (#bceaf9) qui se fondait dans le fond blanc
  // a sky-500 + texte blanc pour bien ressortir partout (badges, marqueurs).
  classik: { bg: '#0ea5e9', color: '#ffffff' },
  spherik: { bg: '#ff0fa4', color: '#ffffff' },
  prestige: { bg: '#dc2626', color: '#ffffff' },
  accessoire: { bg: '#1f845a', color: '#ffffff' },
};

const BORNE_TYPE_COLOR_MAP = {
  classik: '#0ea5e9',
  spherik: '#ff0fa4',
  prestige: '#dc2626',
  accessoire: '#1f845a',
};

const BORNE_TYPE_OPTIONS = [
  { value: 'classik', label: 'Classik' },
  { value: 'spherik', label: 'Spherik' },
  { value: 'prestige', label: 'Prestige' },
];

const QUALIFICATION_COLORS = {
  'antenne_principale': '#5b0a7a',
  'profil_intéressant': '#202b45',
  'historique_antenne': '#4d4d4d',
  'antenne_binome': '#7f1d1d',
};

const QUALIFICATION_BG_COLORS = {
  'antenne_principale': '#bf2dfb',
  'profil_intéressant': '#6687d2',
  'historique_antenne': '#4d4d4d',
  'antenne_binome': '#f98090',
};

const QUALIFICATION_TEXT_COLORS = {
  'antenne_principale': '#ffffff',
  'profil_intéressant': '#202b45',
  'historique_antenne': '#d6d6d6',
  'antenne_binome': '#ffffff',
};

function ContactsList() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState([]);
  const [villes, setVilles] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
  // Nombre de fiches par page calculé d'après la place réellement disponible,
  // et toujours multiple du nombre de colonnes : la dernière rangée est pleine.
  const gridRef = useRef(null);
  const [limit, setLimit] = useState(20);
  // Corbeille : même liste, même recherche, même export — seul le périmètre change.
  const enCorbeille = searchParams.get('deleted') === '1';
  const peutVoirCorbeille = usePerm('pages.corbeille.view');
  const peutRestaurer = usePerm('contacts.restore');
  const peutSupprimerDef = usePerm('contacts.delete_permanent');

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedVilles, setSelectedVilles] = useState(() => {
    const param = searchParams.get('villes');
    return param ? param.split(',') : [];
  });
  const [selectedQualifications, setSelectedQualifications] = useState(() => {
    const param = searchParams.get('qualifications');
    return param ? param.split(',') : (searchParams.toString() === '' ? ['antenne_principale'] : []);
  });
  const [selectedBorneTypes, setSelectedBorneTypes] = useState(() => {
    const param = searchParams.get('borne_types');
    return param ? param.split(',') : [];
  });
  const [appliedBorneTypes, setAppliedBorneTypes] = useState(() => {
    const param = searchParams.get('borne_types');
    return param ? param.split(',') : [];
  });
  const [selectedAnciennetes, setSelectedAnciennetes] = useState(() => {
    const param = searchParams.get('anciennete');
    return param ? param.split(',') : [];
  });
  const [selectedNePlusContacter, setSelectedNePlusContacter] = useState(searchParams.get('ne_plus_contacter') || '');
  const [selectedPrioritaire, setSelectedPrioritaire] = useState(searchParams.get('prioritaire') === 'true');
  const [selectedCodeComptable, setSelectedCodeComptable] = useState(searchParams.get('code_comptable') || '');
  // « Contacts supprimés » : c'est un filtre avancé comme les autres (on le
  // choisit puis on lance la recherche), et non plus un bouton à bascule.
  const [selectedSupprimes, setSelectedSupprimes] = useState(searchParams.get('deleted') === '1');
  const [expandedBornes, setExpandedBornes] = useState(new Set());

  // Arrivée sur /contacts sans rien : on écrit l'état par défaut dans l'URL
  // plutôt que de l'appliquer en douce. L'adresse décrit ce qui est affiché,
  // elle se partage, et revenir dessus redonne la même liste.
  useEffect(() => {
    if ([...searchParams.keys()].length === 0) {
      setSearchParams({ qualifications: 'antenne_principale', sort: 'antenne_ville' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dropdown "Filtres avancés"
  const [advOpen, setAdvOpen] = useState(false);
  const advRef = useRef(null);
  useEffect(() => {
    if (!advOpen) return undefined;
    const onClick = (e) => {
      if (advRef.current && !advRef.current.contains(e.target)) setAdvOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [advOpen]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const page = parseInt(searchParams.get('page')) || 1;
      // Check if any real filter (not just page) is set
      const hasRealFilter = ['search', 'villes', 'qualifications', 'borne_types', 'anciennete', 'ne_plus_contacter', 'prioritaire', 'etat', 'code_comptable'].some(k => searchParams.get(k));

      const params = { page, limit };
      if (searchParams.get('deleted') === '1') params.deleted = 1;
      const s = searchParams.get('search');
      if (s) params.search = s;
      const v = searchParams.get('villes');
      if (v) params.villes = v;
      const q = searchParams.get('qualifications');
      if (q) {
        params.qualifications = q;
      } else if (!hasRealFilter) {
        params.qualifications = 'antenne_principale';
        params.sort = 'antenne_ville';
      }
      const anc = searchParams.get('anciennete');
      if (anc) params.anciennete = anc;
      const npc = searchParams.get('ne_plus_contacter');
      if (npc) params.ne_plus_contacter = npc;
      const bt = searchParams.get('borne_types');
      if (bt) params.borne_types = bt;
      const prio = searchParams.get('prioritaire');
      if (prio) params.prioritaire = prio;
      const codeC = searchParams.get('code_comptable');
      if (codeC) params.code_comptable = codeC;
      // Filtre d'état : utilisé par les cartouches du tableau de bord.
      const et = searchParams.get('etat');
      if (et) params.etat = et;
      // Le tri fait partie de l'état de la liste : il vient de l'URL comme le
      // reste, sinon deux adresses décrivant le même filtre donneraient deux
      // ordres différents.
      const tri = searchParams.get('sort');
      if (tri) params.sort = tri;
      const sens = searchParams.get('order');
      if (sens) params.order = sens;

      const data = await getContacts(params);

      setContacts(data.data);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [searchParams, limit]);


  // Ajuste la pagination à l'écran : on mesure une carte réelle plutôt que de
  // supposer une hauteur, sinon le calcul se décale dès qu'une carte grandit
  // (bornes affichées, badges…). Recalculé au redimensionnement.
  useEffect(() => {
    const calc = () => {
      const el = gridRef.current;
      const card = el && el.querySelector('.contact-card');
      if (!el || !card) return;
      const style = window.getComputedStyle(el);
      const cols = (style.gridTemplateColumns || '').split(' ').filter(Boolean).length || 1;
      const gap = parseFloat(style.rowGap || style.gap || '0') || 0;
      const cardH = card.getBoundingClientRect().height;
      if (!cardH) return;
      // Marge basse : place pour la pagination et un peu d'air.
      const dispo = window.innerHeight - el.getBoundingClientRect().top - 96;
      // Cinq lignes par page : assez pour lire sans paginer sans cesse. On
      // laisse un grand écran en ajouter, jamais en retirer. La limite reste
      // un multiple du nombre de colonnes, donc pas de rangée à trous.
      const tiennent = Math.floor((dispo + gap) / (cardH + gap));
      const rows = Math.min(8, Math.max(5, tiennent));
      const next = cols * rows;
      setLimit((cur) => (cur === next ? cur : next));
    };
    calc();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(calc) : null;
    if (ro && gridRef.current) ro.observe(gridRef.current);
    window.addEventListener('resize', calc);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', calc);
    };
  }, [contacts]);


  const restaurer = async (contact) => {
    try {
      await restoreContact(contact.id);
      toast.success('Fiche restaurée');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Restauration impossible');
    }
  };

  const supprimerDefinitivement = async (contact) => {
    const nom = `${contact.prenom || ''} ${contact.nom || ''}`.trim() || 'cette fiche';
    // Action irréversible : on demande le nom pour éviter le clic réflexe.
    if (!window.confirm(`Supprimer définitivement ${nom} ? Cette action est irréversible.`)) return;
    try {
      await deleteContactPermanent(contact.id);
      toast.success('Fiche supprimée définitivement');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Suppression impossible');
    }
  };

  const loadLookups = async () => {
    try {
      const [qualificationsData, villesData] = await Promise.all([
        getQualifications(),
        getVilles()
      ]);
      setQualifications(qualificationsData);
      setVilles(villesData);
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadData();
      loadLookups();
    }
  }, [auth.user, loadData]);

  // Filters are only applied on search click
  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedBorneTypes([...selectedBorneTypes]);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedVilles.length > 0) params.set('villes', selectedVilles.join(','));
    if (selectedQualifications.length > 0) params.set('qualifications', selectedQualifications.join(','));
    if (selectedBorneTypes.length > 0) params.set('borne_types', selectedBorneTypes.join(','));
    if (selectedAnciennetes.length > 0) params.set('anciennete', selectedAnciennetes.join(','));
    if (selectedNePlusContacter) params.set('ne_plus_contacter', selectedNePlusContacter);
    if (selectedPrioritaire) params.set('prioritaire', 'true');
    if (selectedCodeComptable) params.set('code_comptable', selectedCodeComptable);
    // Réservé aux comptes qui ont le droit : une case cochée dans un navigateur
    // sans le droit ne doit pas pouvoir élargir le périmètre.
    if (selectedSupprimes && peutVoirCorbeille) params.set('deleted', '1');
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedVilles([]);
    // Filtre qualification par defaut = "Antenne actuelle" (pas vide).
    // L'utilisateur peut le retirer manuellement s'il veut vraiment "tous".
    setSelectedQualifications(['antenne_principale']);
    setSelectedBorneTypes([]);
    setAppliedBorneTypes([]);
    setSelectedAnciennetes([]);
    setSelectedNePlusContacter('');
    setSelectedPrioritaire(false);
    setSelectedCodeComptable('');
    setSelectedSupprimes(false);
    setSearchParams({ qualifications: 'antenne_principale', sort: 'antenne_ville' });
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const getQualificationLabel = (qualification) => {
    const labels = {
      'antenne_principale': 'Antenne actuelle',
      'historique_antenne': 'Ancienne antenne',
      'profil_intéressant': 'Profil intéressant',
      'antenne_binome': 'Binôme'
    };
    return labels[qualification] || null;
  };

  const isNewContact = (contact) => {
    const createdDate = contact.created_at || contact.created;
    if (!createdDate) return false;
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    return new Date(createdDate) >= twoMonthsAgo;
  };

  const getAvatarUrl = (contact) => {
    if (contact.photo) return getUploadsUrl(contact.photo);
    return contact.civilite === 'm' ? '/img/avatar-homme.svg' : '/img/avatar-femme.svg';
  };

  const getContactBornes = (contact) => {
    if (contact.bornes_list && Array.isArray(contact.bornes_list)) return contact.bornes_list;
    return [];
  };

  const MAX_VISIBLE_BORNES = 3;

  const getPageNumbers = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [1];
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  };

  const toggleBornesExpand = (e, contactId) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedBornes(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  const filteredContacts = contacts;

  const hasFilters = search || selectedVilles.length > 0 || selectedQualifications.length > 0 || selectedBorneTypes.length > 0 || selectedAnciennetes.length > 0 || selectedNePlusContacter;

  const villeOptions = villes.map(v => ({ value: v, label: v }));
  const qualificationOptions = qualifications.map(q => ({ value: q.value, label: q.label }));
  const ancienneteOptions = [
    { value: '<1', label: '< 1 an' },
    { value: '1-3', label: '1-3 ans' },
    { value: '3-5', label: '3-5 ans' },
    { value: '>5', label: '> 5 ans' },
  ];

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const displayViewMode = 'grid';

  return (
    <div id="contact-lists" className="contacts-page">
      <header className="page-header">
        <div>
          <h1>Contacts antennes</h1>
          <p className="breadcrumb">Tableau de bord / Contacts antennes</p>
        </div>
        <div className="header-actions">
          <Gate perm="contacts.create">
            <Link to="/contacts/new" className="btn btn-primary btn-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Nouveau contact
            </Link>
          </Gate>
        </div>
      </header>

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="filters-row">
          <div className="search-input-wrapper">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch(e);
                }
              }}
              className="search-input"
            />
          </div>

          <MultiSelect
            options={villeOptions}
            selected={selectedVilles}
            onChange={setSelectedVilles}
            placeholder="Villes principales"
            searchable
          />

          <MultiSelect
            options={qualificationOptions}
            selected={selectedQualifications}
            onChange={setSelectedQualifications}
            placeholder="Qualification"
            colorMap={QUALIFICATION_COLORS}
            className="filter-qualification"
          />

          <MultiSelect
            options={BORNE_TYPE_OPTIONS}
            selected={selectedBorneTypes}
            onChange={setSelectedBorneTypes}
            placeholder="Type de borne"
            colorMap={BORNE_TYPE_COLOR_MAP}
          />

          {/* Dropdown "Filtres avancés" — regroupe Prioritaire, Ne plus contacter, Ancienneté */}
          <div className="adv-filters-wrap" ref={advRef}>
            <button
              type="button"
              className={`adv-filters-trigger${(selectedPrioritaire || selectedNePlusContacter || selectedCodeComptable || selectedSupprimes || selectedAnciennetes.length > 0) ? ' has-active' : ''}`}
              onClick={() => setAdvOpen((v) => !v)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="21" x2="4" y2="14" />
                <line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" />
                <line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" />
                <line x1="9" y1="8" x2="15" y2="8" />
                <line x1="17" y1="16" x2="23" y2="16" />
              </svg>
              Filtres avancés
              {(() => {
                const n = (selectedPrioritaire ? 1 : 0) + (selectedNePlusContacter ? 1 : 0)
                  + (selectedCodeComptable ? 1 : 0) + (selectedSupprimes ? 1 : 0)
                  + selectedAnciennetes.length;
                return n > 0 ? <span className="adv-filters-count">{n}</span> : null;
              })()}
            </button>
            {advOpen && (
              <div className="adv-filters-panel">
                <div className="adv-filters-section">
                  <label className="adv-filter-check">
                    <input
                      type="checkbox"
                      checked={selectedPrioritaire}
                      onChange={(e) => setSelectedPrioritaire(e.target.checked)}
                    />
                    <span>⭐ Prioritaires uniquement</span>
                  </label>
                  <label className="adv-filter-check">
                    <input
                      type="checkbox"
                      checked={selectedNePlusContacter === 'oui'}
                      onChange={(e) => setSelectedNePlusContacter(e.target.checked ? 'oui' : '')}
                    />
                    <span>Ne plus contacter</span>
                  </label>
                  <label className="adv-filter-check">
                    <input
                      type="checkbox"
                      checked={selectedCodeComptable === 'absent'}
                      onChange={(e) => setSelectedCodeComptable(e.target.checked ? 'absent' : '')}
                    />
                    <span>Sans code comptable fournisseur</span>
                  </label>
                </div>
                {/* Périmètre : visible seulement pour les comptes qui ont le droit
                    de consulter la corbeille. */}
                {peutVoirCorbeille && (
                  <div className="adv-filters-section">
                    <label className="adv-filter-check">
                      <input
                        type="checkbox"
                        checked={selectedSupprimes}
                        onChange={(e) => setSelectedSupprimes(e.target.checked)}
                      />
                      <span>Contacts supprimés</span>
                    </label>
                  </div>
                )}
                <div className="adv-filters-section">
                  <div className="adv-filters-label">Ancienneté</div>
                  {ancienneteOptions.map((opt) => {
                    const isOn = selectedAnciennetes.includes(opt.value);
                    return (
                      <label key={opt.value} className="adv-filter-check">
                        <input
                          type="checkbox"
                          checked={isOn}
                          onChange={() => {
                            setSelectedAnciennetes(
                              isOn ? selectedAnciennetes.filter((v) => v !== opt.value)
                                   : [...selectedAnciennetes, opt.value]
                            );
                          }}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <button type="submit" className="btn-search" title="Rechercher">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          <button type="button" onClick={clearFilters} className="btn-reset" title="Réinitialiser les filtres">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>
        </form>
      </div>

      {/* Toolbar */}
      <div className="contacts-toolbar">
        <span className="results-count">
          <strong>{pagination.total}</strong> contact{pagination.total !== 1 ? 's' : ''}
          {hasFilters ? ' trouvé' + (pagination.total !== 1 ? 's' : '') : ''}
        </span>
      </div>

      {enCorbeille && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', fontSize: 13 }}>
          Contacts supprimés : ces fiches sont masquées partout ailleurs dans l’application.
          Elles restent exportables, restaurables, ou supprimables définitivement.
          Décochez « Contacts supprimés » dans les filtres avancés pour revenir aux fiches actives.
        </div>
      )}

      {/* Contacts Grid/List */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          Chargement...
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
          </svg>
          <h3>Aucun contact trouvé</h3>
          <p>Modifiez vos filtres ou ajoutez un nouveau contact</p>
          <Gate perm="contacts.create"><Link to="/contacts/new" className="btn btn-primary btn-sm">Ajouter un contact</Link></Gate>
        </div>
      ) : (
        <div className={`contacts-${displayViewMode}`} ref={gridRef}>
          {filteredContacts.map(contact => {
            const qualLabel = getQualificationLabel(contact.qualification);
            const qualBg = QUALIFICATION_BG_COLORS[contact.qualification];
            const qualText = QUALIFICATION_TEXT_COLORS[contact.qualification];
            const isNew = isNewContact(contact);
            const bornes = getContactBornes(contact);
            const borneTypes = bornes.length > 0
              ? Object.entries(bornes.reduce((acc, b) => {
                  const type = b.type_borne || 'autre';
                  acc[type] = (acc[type] || 0) + 1;
                  return acc;
                }, {}))
              : [];
            return (
              <Link to={`/contacts/${contact.id}`} key={contact.id} className="contact-card">
                {enCorbeille && (peutRestaurer || peutSupprimerDef) && (
                  <div
                    className="corbeille-actions"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  >
                    {peutRestaurer && (
                      <button type="button" onClick={() => restaurer(contact)} className="btn btn-secondary btn-xs">
                        Restaurer
                      </button>
                    )}
                    {peutSupprimerDef && (
                      <button type="button" onClick={() => supprimerDefinitivement(contact)} className="btn btn-danger btn-xs">
                        Supprimer définitivement
                      </button>
                    )}
                  </div>
                )}
                {/* Bandeau qualification */}
                <div
                  className="contact-card-header"
                  style={qualBg ? { background: qualBg, color: qualText } : {}}
                >
                  <span className="card-header-city" style={qualText ? { color: qualText } : {}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {contact.antenne_ville || contact.ville || 'Non renseigné'}
                  </span>
                  <span className="card-header-right">
                    {isNew && (
                      <span className="contact-new-badge">Nouveau</span>
                    )}
                    {qualLabel && (
                      <span className="contact-qualification-tag" style={{ color: qualText }}>
                        {qualLabel}
                      </span>
                    )}
                  </span>
                </div>

                {/* Contenu carte */}
                <div className="contact-card-body">
                  <div className="contact-avatar">
                    <img src={getAvatarUrl(contact)} alt={`${contact.prenom} ${contact.nom}`} />
                  </div>
                  <div className="contact-info">
                    {/* Prénom NOM (avec ⭐ si prioritaire) */}
                    <h3 className="contact-name">
                      {contact.prioritaire && (
                        <span
                          title="Contact prioritaire"
                          aria-label="Prioritaire"
                          style={{ fontSize: 14, lineHeight: 1, marginRight: 4 }}
                        >
                          ⭐
                        </span>
                      )}
                      {contact.prenom} <span className="contact-nom-uppercase">{(contact.nom || '').toUpperCase()}</span>
                    </h3>

                    {/* Raison sociale */}
                    {contact.raison_sociale && (
                      <p className="contact-company">{contact.raison_sociale}</p>
                    )}

                    {/* Types de bornes + Accessoires sur la même ligne */}
                    {(borneTypes.length > 0 || (contact.accessoires_list && contact.accessoires_list.length > 0)) && (
                      <div className="contact-bornes-info">
                        <span className="bornes-types">
                          {borneTypes.map(([type, count]) => {
                            const tc = BORNE_TYPE_COLORS[type] || { bg: '#e2e8f0', color: '#475569' };
                            return (
                              <span
                                key={type}
                                className="borne-type-chip"
                                style={{
                                  background: tc.bg,
                                  color: tc.color,
                                  borderColor: tc.bg
                                }}
                              >
                                {count} {type.charAt(0).toUpperCase() + type.slice(1)}
                              </span>
                            );
                          })}
                          {contact.accessoires_list && Array.isArray(contact.accessoires_list) && contact.accessoires_list.map((acc, i) => (
                            <span
                              key={`acc-${i}`}
                              className="accessoire-chip"
                              style={{
                                background: acc.type_equipement_couleur || '#276cb8',
                                color: '#ffffff'
                              }}
                            >
                              {acc.count > 1 && (
                                <span className="accessoire-count">{acc.count}</span>
                              )}
                              {acc.equipement_valeur}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}

                    {/* Numéros de bornes */}
                    {bornes.length > 0 && (
                      <p className="contact-detail-line bornes-line">
                        <span className="detail-label">Bornes :</span>
                        {(expandedBornes.has(contact.id) ? bornes : bornes.slice(0, MAX_VISIBLE_BORNES)).map((b, i) => {
                          const bc = BORNE_TYPE_COLORS[b.type_borne] || { bg: '#475569', color: '#475569' };
                          return (
                            <span key={b.id}>
                              {i > 0 && ', '}
                              <span style={{ color: bc.bg, fontWeight: 600 }}>
                                {b.numero_formated}
                              </span>
                            </span>
                          );
                        })}
                        {bornes.length > MAX_VISIBLE_BORNES && (
                          <span
                            className="bornes-toggle-btn"
                            onClick={(e) => toggleBornesExpand(e, contact.id)}
                          >
                            {expandedBornes.has(contact.id) ? ' voir moins' : `.. voir + (${bornes.length - MAX_VISIBLE_BORNES})`}
                          </span>
                        )}
                      </p>
                    )}

                    {/* Forfait livraison client - uniquement antenne principale ou historique */}
                    {(contact.qualification === 'antenne_principale' || contact.qualification === 'historique_antenne') && (
                    <p className={`contact-delivery-badge ${contact.accept_delivery ? 'delivery-yes' : 'delivery-no'}`}>
                      {contact.accept_delivery ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      )}
                      Forfait livraison client : {contact.accept_delivery ? 'Oui' : 'Non'}
                    </p>
                    )}

                    {/* Ne plus contacter - en bas */}
                    {contact.ne_plus_contacter && (
                      <p className="contact-warning-small">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        Ne plus contacter
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="pagination">
          <span className="pagination-info">
            {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total}
          </span>
          <div className="pagination-buttons">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="pagination-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {getPageNumbers(pagination.page, totalPages).map((p, i) =>
              p === '...'
                ? <span key={`dots-${i}`} className="pagination-dots">...</span>
                : <button
                    key={p}
                    className={`pagination-page-btn ${p === pagination.page ? 'active' : ''}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
            )}
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
              className="pagination-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsList;
